"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ChevronLeft, ArrowRight } from "lucide-react";
import Link from "next/link";

const INGREDIENTS = [
  { name: "Beef chuck (3 oz)", cost: 1.20, emoji: "🥩" },
  { name: "Corn tortilla × 2", cost: 0.18, emoji: "🌮" },
  { name: "Onion · cilantro", cost: 0.08, emoji: "🧅" },
  { name: "Salsa roja (1 oz)", cost: 0.14, emoji: "🌶️" },
  { name: "Lime wedge", cost: 0.05, emoji: "🍋" },
  { name: "Foil wrap", cost: 0.06, emoji: "📦" },
];

const SELL_PRICE = 4.50;

export default function InventoryHero() {
  const { t } = useLang();
  const cogs = INGREDIENTS.reduce((s, i) => s + i.cost, 0);
  const margin = SELL_PRICE - cogs;
  const marginPct = (margin / SELL_PRICE) * 100;

  return (
    <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-[#FAFAF8]">
      <div
        className="absolute top-0 right-0 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.10) 0%, transparent 70%)" }}
      />
      <div
        className="absolute bottom-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(215,38,61,0.06) 0%, transparent 70%)" }}
      />

      <div className="relative max-w-[1280px] mx-auto">
        <Link
          href="/features"
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-redy-secondary hover:text-redy-charcoal transition-colors mb-10"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t("All features", "Todas las features")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-black tracking-widest uppercase mb-6 text-center"
          style={{ color: "#D7263D" }}
        >
          {t("Inventory & COGS · Recipe-level", "Inventario & COGS · A nivel receta")}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-[44px] sm:text-6xl md:text-[88px] lg:text-[110px] font-black leading-[0.95] tracking-[-0.04em] mb-6 text-redy-charcoal"
        >
          {t("Recipes.", "Recetas.")}
          <br />
          <span
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {t("Exploded.", "Desarmadas.")}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[17px] md:text-[20px] font-medium text-redy-secondary max-w-[680px] mx-auto leading-relaxed mb-12"
        >
          {t(
            "Every plate broken down into the ingredients it took to make it. Real cost-of-goods, not the one you guessed at last year. Sell a taco, the beef inventory drops by 3 oz. Real-time, recipe-level.",
            "Cada plato desarmado en los ingredientes que tomó hacerlo. Cost-of-goods real, no el que adivinaste el año pasado. Vendes un taco, el inventario de carne baja 3 oz. En vivo, a nivel receta."
          )}
        </motion.p>

        {/* Exploded view: dish in center, ingredients radiating out */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white border border-black/5 rounded-[32px] p-8 md:p-12 max-w-[920px] mx-auto shadow-[0_30px_80px_rgba(0,0,0,0.06)] mb-12 relative overflow-hidden"
        >
          {/* Background atmosphere */}
          <div
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              background: "radial-gradient(circle at center, rgba(244,114,43,0.10) 0%, transparent 70%)",
            }}
          />

          <div className="relative">
            <div className="text-center mb-8">
              <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-2">
                {t("Menu item", "Item del menú")}
              </div>
              <div className="text-[40px] md:text-[52px] font-black text-redy-charcoal tracking-tight leading-none">
                Birria Taco
              </div>
              <div className="text-[12px] font-bold text-redy-secondary mt-2">
                {t("Sells for", "Se vende a")}{" "}
                <span
                  className="text-redy-charcoal tabular-nums"
                  style={{ fontSize: "18px" }}
                >
                  ${SELL_PRICE.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Ingredient grid (exploded view) */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
              {INGREDIENTS.map((ing, i) => (
                <motion.div
                  key={ing.name}
                  initial={{ opacity: 0, scale: 0.7, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{
                    duration: 0.5,
                    delay: 0.8 + 0.1 * i,
                    ease: [0.21, 0.47, 0.32, 0.98],
                  }}
                  className="bg-[#FAFAF8] border border-black/5 rounded-[14px] p-3 flex items-center gap-2.5"
                >
                  <div className="text-[20px]">{ing.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-bold text-redy-charcoal truncate">{ing.name}</div>
                    <div className="text-[13px] font-black text-redy-charcoal tabular-nums" style={{
                      background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      color: "transparent",
                    }}>
                      ${ing.cost.toFixed(2)}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* COGS + margin breakdown */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.7 }}
              className="grid grid-cols-3 gap-3"
            >
              <div className="bg-[#FAFAF8] border border-black/5 rounded-[16px] p-4 text-center">
                <div className="text-[9px] font-black tracking-widest uppercase text-redy-secondary/60 mb-1">
                  {t("COGS", "COGS")}
                </div>
                <div className="text-[24px] font-black text-redy-charcoal tabular-nums">
                  ${cogs.toFixed(2)}
                </div>
              </div>
              <div className="bg-[#FAFAF8] border border-black/5 rounded-[16px] p-4 text-center">
                <div className="text-[9px] font-black tracking-widest uppercase text-redy-secondary/60 mb-1">
                  {t("Margin", "Margen")}
                </div>
                <div
                  className="text-[24px] font-black tabular-nums"
                  style={{
                    background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  ${margin.toFixed(2)}
                </div>
              </div>
              <div
                className="rounded-[16px] p-4 text-center text-white"
                style={{
                  background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                }}
              >
                <div className="text-[9px] font-black tracking-widest uppercase text-white/75 mb-1">
                  {t("Margin %", "Margen %")}
                </div>
                <div className="text-[24px] font-black tabular-nums">
                  {marginPct.toFixed(0)}%
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.4 }}
          className="text-center"
        >
          <Link
            href="/signup"
            className="group inline-flex items-center gap-2 text-[15px] font-bold px-8 py-4 rounded-full hover:scale-[1.03] transition-transform shadow-2xl text-white"
            style={{
              background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
              boxShadow: "0 20px 60px rgba(244, 114, 43, 0.4)",
            }}
          >
            {t("Know your real food cost →", "Conoce tu food cost real →")}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
